import { NO_DATA_PLACEHOLDER } from 'constants/labels';
import React, { useContext } from 'react';

import { Table } from '../../components/Table';
import {
  centenaryNumberFormatter,
  contactListingAddress,
  getFormattedPriceChangeInDollars,
  getListingUnitIdentifier,
  priceFormatter,
  sqftFormatter,
} from '../../src/utils/utils';
import {
  UnitPageContext,
  UnitPageContextInterface,
} from '../../pages/[urlParam]/[urlBuildParam]/[urlUnitParam]';
import ErrorBoundary from 'components/ErrorBoundary';
import PopUpContactUs from 'components/ContactUs/PopUpContactUs';
import { hasListedPrice } from 'lib/checkListingStatus';
import BathsCount from 'components/BathsCount';
import { getClosedStatusLabel } from 'lib/getClosedStatusLabel';

const PRICE_ESTIMATE_PILL_CLASSNAME =
  'md:h-10 px-1 py-1 text-12-18-0.3 w-18 md:px-3 md:text-18-28-0.3 bg-gold-dark';

const TableUnit: React.FC = () => {
  const { listing, building, neighborhood } = useContext(
    UnitPageContext
  ) as UnitPageContextInterface;
  const {
    statusCode,
    propertyTypeCode,
    price,
    bedsTotal,
    sqft,
    sqftLivingArea,
    yearBuilt,
    previousPrice,
  } = listing;

  const labelPrice = (() => {
    if (statusCode === 'CS' || statusCode === 'R') {
      return getClosedStatusLabel(propertyTypeCode, statusCode).toUpperCase();
    } else {
      return `LIST`;
    }
  })();

  function renderPriceDelta() {
    if (
      !hasListedPrice(statusCode) ||
      !previousPrice ||
      previousPrice <= 0 ||
      price === previousPrice ||
      price / previousPrice >= 3 ||
      !+centenaryNumberFormatter(
        ((price - previousPrice) * 100.0) / previousPrice
      )
    )
      return null;

    const delta = getFormattedPriceChangeInDollars(price, previousPrice);

    return (
      <div
        title={`Was ${priceFormatter(previousPrice)}`}
        className={`text-12-18-0.3 md:text-16-26-0.3 ${
          delta.includes('-') ? 'text-red' : 'text-azure'
        } font-sohneHalbfett`}
      >
        Price {delta.includes('-') ? 'Reduced' : 'Increased'}: {delta}
      </div>
    );
  }

  function renderSalePrice(): any {
    return hasListedPrice(statusCode) ? price : renderPriceEstimate();
  }

  function renderPriceEstimate() {
    if (propertyTypeCode === 'R') {
      const unitIdentifier = getListingUnitIdentifier(listing);
      const unitAddress = contactListingAddress(
        listing?.address,
        neighborhood?.city?.data?.attributes?.name || ''
      );
      const unitLocation = [building?.name, unitAddress]
        .filter(Boolean)
        .join(', ');

      return (
        <PopUpContactUs
          contactUsNotButton="Price estimate"
          contactUsNotButtonClassName={PRICE_ESTIMATE_PILL_CLASSNAME}
          customText={`I'm interested in unit${
            unitIdentifier ? ` #${unitIdentifier}` : ''
          }${
            unitLocation ? ` at ${unitLocation}` : ''
          } and would like to get a price estimate for it.`}
        />
      );
    }

    return (
      <a
        href="#condo-value-estimates"
        className="text-12-18-0.3 md:text-18-28-0.3"
      >
        <div className={PRICE_ESTIMATE_PILL_CLASSNAME}>Price estimate</div>
      </a>
    );
  }

  return (
    <ErrorBoundary>
      <div
        id="RentOrSale"
        className="absolute top-[-120px] md:top-[-130px] xl:top-[-80px]"
      />
      <section className="container mt-[20px] md:mt-[6px] md:px-12 md:pt-5 md:pb-2 lg:py-2 text-m">
        <Table
          rootClassName="no-scroll flex flex-row flex-nowrap "
          rowClasses="font-sohneHalbfett text-14-26-0.3 md:text-20-28-0.3 lg:text-30-36-1 md:mt-4 md:tracking-widest"
          colClasses="!pb-0 md:pb-[32px] lg:pb-[3px] mb-[13px] md:mb-4 "
          columns={[
            {
              key: 'price',
              heading: `${labelPrice} PRICE`,
              headingMobile: `${labelPrice} PRICE`,
              widthRatio: 2,
              cellRender: (row) => (
                <div>
                  <div className="flex flex-col">
                    <div className="flex flex-col justify-center md:flex-row">
                      <div className="flex justify-center">
                        <div className="pt-px md:pt-0.7 font-sohneHalbfett">
                          {renderSalePrice() > 0
                            ? hasListedPrice(statusCode)
                              ? priceFormatter(renderSalePrice())
                              : `$ ${NO_DATA_PLACEHOLDER}`
                            : renderPriceEstimate()}
                        </div>
                      </div>
                    </div>
                    {renderPriceDelta()}
                  </div>
                </div>
              ),
            },
            {
              key: 'bedrooms',
              headingMobile: 'BEDS',
            },
            {
              key: 'bathrooms',
              headingMobile: 'BATHS',
            },
            {
              key: 'area',
              heading: 'AREA sq ft',
              headingMobile: 'SQ FT',
              cellRender: (row) => {
                return (
                  <div>
                    <div>
                      {row.area ? sqftFormatter(row.area) : NO_DATA_PLACEHOLDER}
                    </div>
                  </div>
                );
              },
            },
            {
              key: 'year',
              heading: 'YEAR BUILT',
              headingMobile: 'BUILT',
            },
          ]}
          data={[
            {
              id: 1,
              salePrice: renderSalePrice(),
              priceDelta: renderPriceDelta(),
              bedrooms: bedsTotal ?? NO_DATA_PLACEHOLDER,
              bathrooms: <BathsCount listing={listing} />,
              area: sqftLivingArea || sqft,
              year: yearBuilt || NO_DATA_PLACEHOLDER,
            },
          ]}
        />
      </section>
    </ErrorBoundary>
  );
};

export default TableUnit;
